import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { getContactStatusColor } from "@/utils/common"
import { MapPin, Mail, MessageSquare, Calendar, Globe, Rocket, Activity, CheckCircle2, AlertCircle, XCircle, Info, User, ExternalLink, Clock, Smartphone, Monitor, Tablet, Layers, Phone, Building2, UserCircle, ListChecks, ShieldCheck, MailX, HelpCircle, RefreshCcw, PlusCircle, RefreshCw, ListPlus, ListX, Trash2, Archive, RotateCcw, Send, AlertTriangle, FastForward, History } from "lucide-react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { useContacts } from "@/hooks/useContacts"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { useToast } from "@/hooks/use-toast"
import { apiFetch } from "@/lib/api/apiFetch"
import { Loader2 } from "lucide-react"
import clsx from "clsx"
import { Button } from "./ui/button"
import ActivityTimelineTab from "./ActivityTimelineTab"
import { EMAIL_STATUS } from "@/utils/email-status"

interface Contact {
  id: string
  email: string
  name: string
  source: any
  location?: string
  status: any
  remarks?: string
  createdAt: string
  updatedAt: string
  isActive: boolean
  phone?: string
  company?: string
  role?: string
  emailStatus?: string
  lists?: Array<{ id: string, name: string }>
}

interface ContactDetailsProps {
  contact: Contact
  refreshContacts?: () => Promise<void>
}

const renderEventIcon = (status: string) => {
  const s = status.toLowerCase();
  switch (s) {
    // Contact Actions
    case "contact_created": return <PlusCircle className="h-4 w-4 text-emerald-500" />;
    case "contact_updated": return <RefreshCw className="h-4 w-4 text-blue-500" />;
    case "status_changed": return <Activity className="h-4 w-4 text-amber-500" />;
    case "deleted": return <Trash2 className="h-4 w-4 text-rose-500" />;

    // List Actions
    case "added_to_list": return <ListPlus className="h-4 w-4 text-emerald-500" />;
    case "removed_from_list": return <ListX className="h-4 w-4 text-rose-500" />;

    // Email/Verification Actions
    case "email_status_changed": return <ShieldCheck className="h-4 w-4 text-blue-500" />;
    case "reverified_email": return <History className="h-4 w-4 text-purple-500" />;
    case "email_verified": return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    case "email_archived": return <Archive className="h-4 w-4 text-zinc-500" />;
    case "email_restored": return <RotateCcw className="h-4 w-4 text-blue-400" />;

    // Campaign Actions
    case "campaign_sent": return <Send className="h-4 w-4 text-primary" />;
    case "campaign_failed": return <AlertTriangle className="h-4 w-4 text-rose-600" />;
    case "campaign_skipped": return <FastForward className="h-4 w-4 text-amber-500" />;

    // Legacy/Old actions (if any)
    case "sent": return <Rocket className="h-4 w-4 text-primary" />;
    case "delivered": return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    case "open": return <Globe className="h-4 w-4 text-blue-500" />;
    case "click": return <ExternalLink className="h-4 w-4 text-purple-500" />;
    case "bounce": return <XCircle className="h-4 w-4 text-rose-500" />;
    case "failed": return <AlertCircle className="h-4 w-4 text-rose-600" />;
    case "skipped": return <ShieldCheck className="h-4 w-4 text-amber-500" />;
    case "unsubscribe": return <User className="h-4 w-4 text-zinc-500" />;

    default: return <Activity className="h-4 w-4 text-zinc-400" />;
  }
};

const renderDeviceIcon = (device: string) => {
  const d = device.toLowerCase();
  if (d.includes("mobile") || d.includes("phone") || d.includes("android") || d.includes("ios")) return <Smartphone className="h-3 w-3" />;
  if (d.includes("tablet") || d.includes("ipad")) return <Tablet className="h-3 w-3" />;
  return <Monitor className="h-3 w-3" />;
};

const renderEventData = (log: any) => {
  return (
    <div className="flex flex-col gap-2 mt-1">
      <p className="text-md text-zinc-600 dark:text-zinc-400 leading-relaxed">
        {log.message || "Activity recorded successfully."}
      </p>
    </div>
  );
};

export function ContactDetails({ contact, refreshContacts }: ContactDetailsProps) {
  const { toast } = useToast()
  const [updatingEmailStatus, setUpdatingEmailStatus] = useState(false)
  const [currentContact, setCurrentContact] = useState(contact)
  const [reverifyingEmail, setReverifyingEmail] = useState(false)
  const [emailStatusUpdatable, setEmailStatusUpdatable] = useState(false)

  useEffect(() => {
    setCurrentContact(contact)
    setEmailStatusUpdatable(!contact.isActive)
  }, [contact])

  const { fetchContactActivity, contactActivity, loading, activityPagination } = useContacts()

  useEffect(() => {
    if (contact?.id) {
      fetchContactActivity(contact.id)
    }
  }, [fetchContactActivity, contact?.id])

  const handleUpdateEmailStatus = async (newStatus: string) => {
    try {
      setUpdatingEmailStatus(true)

      const response = await apiFetch(`/api/contacts/${contact.id}/email-status`, {
        method: "PUT",
        data: {
          emailStatus: newStatus
        }
      })

      if (response && !response.error) {
        if (refreshContacts) {
          await refreshContacts()
        }
        setCurrentContact(prev => ({ ...prev, emailStatus: newStatus }))
        toast({
          title: "Email status updated",
          description: `Email status is now ${newStatus.replace("_", " ")}.`
        })
      }
    } catch (error: any) {
      toast({
        title: "Error updating email status",
        description: error.message || "Something went wrong",
        variant: "destructive"
      })
    } finally {
      setUpdatingEmailStatus(false)
    }
  }

  const handleReverifyEmail = async (email: string) => {
    try {
      setReverifyingEmail(true)

      const response = await apiFetch(`/api/contacts/${contact.id}/re-verify`, {
        method: "PUT",
        data: {
          email
        }
      })

      if (response && !response.error) {
        if (refreshContacts) {
          await refreshContacts()
        }
        setCurrentContact(prev => ({ ...prev, emailStatus: "unverified" }))
        await fetchContactActivity(contact.id)
        toast({
          title: "Re-verification started",
          description: "Email marked as unverified. Verification is running in the background."
        })
      }
    } catch (error: any) {
      toast({
        title: "Error updating email status",
        description: error.message || "Something went wrong",
        variant: "destructive"
      })
    } finally {
      setReverifyingEmail(false)
    }
  }

  const statusClasses: Record<string, string> = {
    safe: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    unsafe: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    bounced: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    invalid: "bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300",
    risky: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    verification_failed: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    unverified: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getTimeAgo = (dateString: string) => {
    const seconds = Math.floor((new Date().getTime() - new Date(dateString).getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  const isBounced = contact.emailStatus?.toLowerCase() === "bounced"

  const emailStatus =
    currentContact?.emailStatus &&
      currentContact.emailStatus in EMAIL_STATUS
      ? (currentContact.emailStatus as keyof typeof EMAIL_STATUS)
      : "unverified";

  const emailStatusConfig = EMAIL_STATUS[emailStatus];

  const EMAIL_STATUS_OPTIONS = [
    "safe",
    "unsafe",
    "risky",
    "bounced",
    "invalid",
    "verification_failed",
    "unverified",
    "unsubscribed",
  ] as const;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="h-full flex flex-col pt-0 overflow-hidden"
    >
      <Tabs defaultValue="userDetails" className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="px-6 pt-6 pb-2 bg-white/95 dark:bg-zinc-950/95 sticky top-0 z-20 border-b border-zinc-100 dark:border-zinc-800/50">
          <TabsList className="grid w-full grid-cols-2 p-1 bg-zinc-100 dark:bg-zinc-900 rounded-lg">
            <TabsTrigger value="userDetails" className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:shadow-sm transition-all py-2 text-xs font-semibold text-zinc-500 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-50">
              Profile Details
            </TabsTrigger>
            <TabsTrigger value="userActivityLogs" className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:shadow-sm transition-all py-2 text-xs font-semibold text-zinc-500 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-50">
              Activity
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden px-6">
          <TabsContent value="userDetails" className="h-full mt-0 focus-visible:outline-none overflow-hidden">
            <div className="h-full overflow-y-auto hide-scrollbar">
              <div className="flex flex-col gap-8 pb-24">
                {/* 1. Cleaner Profile Header */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-4 p-4 mt-4 rounded-xl border border-zinc-200/60 dark:border-zinc-800/60 bg-white/70 dark:bg-zinc-900/50 backdrop-blur-sm"
                >
                  {/* LEFT SECTION */}
                  <div className="flex items-center gap-3 min-w-0">

                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center font-semibold text-zinc-700 dark:text-zinc-300">
                        {contact.name
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>

                      {/* Status Dot */}
                      <div
                        className={cn(
                          "absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-white dark:border-zinc-900",
                          contact.isActive ? "bg-emerald-500" : "bg-rose-500"
                        )}
                      />
                    </div>

                    {/* Info */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold truncate text-zinc-900 dark:text-zinc-50">
                          {contact.name || "Unnamed Contact"}
                        </h2>

                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] px-2 py-0.5 rounded-md",
                            contact.isActive
                              ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400"
                              : "bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400"
                          )}
                        >
                          {contact.isActive ? "Active" : "Disabled"}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="h-3.5 w-3.5" />
                          {contact.email}
                        </span>

                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="h-3.5 w-3.5" />
                          {contact.location || "Unknown"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SECTION (Optional actions) */}
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Add buttons if needed */}
                    {/* here implement revarify email button */}


                    <Button
                      onClick={() => handleReverifyEmail(contact.email)}
                      disabled={reverifyingEmail || isBounced || !contact.isActive}
                      title={isBounced ? "Reverification is not allowed for bounced emails" : !contact.isActive ? "Reverification is not allowed for inactive contacts" : undefined}
                      className="
                          bg-gradient-to-r from-blue-500 to-indigo-500
                          hover:from-blue-600 hover:to-indigo-600
                          text-white
                          shadow-md shadow-blue-500/20
                          border border-blue-400/20
                          dark:shadow-blue-500/10
                          disabled:opacity-60 disabled:cursor-not-allowed
                      "
                    >
                      {reverifyingEmail ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Reverifying...
                        </>
                      ) : (
                        <>
                          <RefreshCcw className="h-4 w-4" />
                          Reverify Email
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>

                {/* 2. Structured Information Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-5 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-4 shadow-sm"
                  >
                    <div className="flex items-center gap-2 text-zinc-500">
                      <UserCircle className="h-4 w-4 text-zinc-400" />
                      <span className="text-xs font-semibold">Contact Info</span>
                    </div>
                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-zinc-500 mb-1">Phone Number</span>
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-50">{contact.phone || "Not provided"}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs text-zinc-500 mb-1">Identity Role</span>
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-50 capitalize">{contact.role || "Standard User"}</span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-5 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-4 shadow-sm"
                  >
                    <div className="flex items-center gap-2 text-zinc-500">
                      <Building2 className="h-4 w-4 text-zinc-400" />
                      <span className="text-xs font-semibold">Organization</span>
                    </div>
                    <div className="flex flex-col mt-2">
                      <span className="text-xs text-zinc-500 mb-1">Company / Affiliation</span>
                      <span className="text-base font-semibold text-zinc-900 dark:text-zinc-50">{contact.company || "Independent"}</span>
                    </div>
                  </motion.div>
                </div>

                {/* 3. Technical Metadata Tiles */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-zinc-500">
                    <Layers className="h-4 w-4 text-zinc-400" />
                    <h3 className="text-xs font-semibold">Technical Details</h3>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { icon: Rocket, label: "Source", value: currentContact.source?.name || "Manual" },
                      { icon: Activity, label: "Status", value: currentContact.status?.name || "N/A" },
                      {
                        icon: emailStatusConfig.icon,
                        label: "Email Status",
                        value: emailStatusConfig.label,
                        isStatus: true,
                        color: emailStatusConfig.text,
                      },
                      { icon: Calendar, label: "Discovery", value: new Date(currentContact.createdAt).toLocaleDateString() },
                    ].map((item: any, idx) => (

                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 + idx * 0.1 }}
                        className="bg-zinc-50 dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800 flex flex-col justify-between overflow-hidden"
                      >
                        <div>
                          <item.icon className={cn("h-4 w-4 mb-3", item.color || "text-zinc-400")} />
                          <div className="space-y-1">
                            <p className="text-xs text-zinc-500 font-medium">{item.label}</p>
                            {item.isStatus ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={updatingEmailStatus}>
                                  <Badge
                                    className={cn(
                                      "mt-1 flex w-fit items-center gap-1 px-2 py-0.5 text-[10px] font-bold capitalize cursor-pointer hover:opacity-80",
                                      emailStatusConfig.badge
                                    )}
                                  >
                                    {updatingEmailStatus ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <>
                                        <item.icon className="h-3 w-3" />
                                        {emailStatusConfig.label}
                                      </>
                                    )}
                                  </Badge>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-40 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-xl z-50">
                                  {EMAIL_STATUS_OPTIONS.map((status) => (
                                    <DropdownMenuItem
                                      key={status}
                                      onClick={() => handleUpdateEmailStatus(status)}
                                      className={clsx(
                                        "cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 capitalize py-2 text-xs",
                                        currentContact.emailStatus === status && "bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-50 font-medium"
                                      )}
                                    >
                                      {status.replace("_", " ")}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <p className={cn("text-sm font-semibold truncate capitalize", item.color ? item.color : "text-zinc-900 dark:text-zinc-100")}>{item.value}</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* 2.5 List Memberships */}
                {contact.lists && contact.lists.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="px-1"
                  >
                    <div className="p-5 bg-zinc-50 dark:bg-zinc-900/50 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-3">
                      <div className="flex items-center gap-2 text-zinc-500">
                        <ListChecks className="h-4 w-4" />
                        <span className="text-xs font-semibold">Active Lists</span>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {contact.lists.map((list) => (
                          <Badge
                            key={list.id}
                            variant="secondary"
                            className="font-medium bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 px-3 py-1 rounded-md"
                          >
                            {list.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 3. Remarks Callout */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="px-1"
                >
                  <div className="p-5 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-[0.03] dark:opacity-5">
                      <MessageSquare className="h-24 w-24" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="h-4 w-4 text-zinc-400" />
                      <h3 className="text-xs font-semibold text-zinc-500">Remarks</h3>
                    </div>
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed max-w-lg">
                      {contact.remarks ? contact.remarks : "No additional remarks provided."}
                    </p>
                  </div>
                </motion.div>

                {/* 5. Timeline Footer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="px-6 py-4 flex flex-col items-center gap-3 border-t border-zinc-100 dark:border-zinc-800 mt-4"
                >
                  <div className="flex items-center gap-4 text-[11px] text-zinc-400 font-bold uppercase tracking-widest">
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Updated {getTimeAgo(contact.updatedAt)}</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="userActivityLogs" className="h-full mt-0 focus-visible:outline-none overflow-hidden">
            <ActivityTimelineTab
              contactActivity={contactActivity}
              loading={loading}
              activityPagination={activityPagination}
              fetchContactActivity={fetchContactActivity}
              contactId={contact.id}
              renderEventIcon={renderEventIcon}
              renderEventData={renderEventData}
              getTimeAgo={getTimeAgo}
            />
          </TabsContent>
        </div>
      </Tabs>
    </motion.div>
  )
}

